import { motion } from "framer-motion";
import {
  CandlestickChart,
  LineChart,
  Droplets,
  CreditCard,
  Bitcoin,
  Cpu,
} from "lucide-react";

const ITEMS = [
  { icon: CandlestickChart, title: "Forex Brokers", tag: "FX · MT4/MT5 · Liquidity", desc: "Retail & institutional brokerages scaling sales, dealing and risk.", accent: "green",  color: "#D9F99D" },
  { icon: LineChart,        title: "Prop Trading Firms", tag: "Prop · HFT · Algo",          desc: "Quant, systematic and discretionary talent for performance desks.", accent: "purple", color: "#A78BFA" },
  { icon: Droplets,         title: "Liquidity Providers", tag: "LP · Market Making",        desc: "Tier‑1 market makers and non‑bank LPs across FX, crypto & equities.", accent: "blue",   color: "#60A5FA" },
  { icon: CreditCard,       title: "Payment Gateways", tag: "PSP · Acquiring · BaaS",       desc: "Cross‑border payments, compliance, infra and commercial teams.", accent: "yellow", color: "#FDE68A" },
  { icon: Bitcoin,          title: "Web3 / Crypto", tag: "CEX · DEX · DeFi",                desc: "Exchanges, custodians, protocols and on‑chain infra builders.", accent: "orange", color: "#FDBA74" },
  { icon: Cpu,              title: "AI Companies", tag: "ML · LLM · MLOps",                 desc: "Frontier model teams, applied AI and quant‑AI cross‑overs.", accent: "cyan",   color: "#67E8F9" },
];

export default function Industries() {
  return (
    <section
      id="industries"
      data-testid="industries-section"
      className="relative py-20 sm:py-24 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#D9F99D]/90">
              Coverage / Industries
            </div>
            <h2
              data-testid="industries-heading"
              className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white"
            >
              Where we operate — <span className="zx-hi">deep.</span>
            </h2>
          </div>
          <p className="max-w-md text-neutral-200">
            Sector specialists across the full capital markets &amp; digital
            finance value chain. We know your stack, your regulators and your
            competitors.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {ITEMS.map((it, i) => {
            const p = { accent: it.accent, color: it.color };
            return (
              <motion.article
                key={it.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                data-testid={`industry-card-${i}`}
                data-accent={p.accent}
                className="zx-card group p-7"
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute -top-20 -right-16 w-56 h-56 rounded-full opacity-60 transition-opacity duration-500 group-hover:opacity-100"
                  style={{ background: `radial-gradient(circle, ${p.color}26, transparent 70%)`, filter: "blur(28px)" }}
                />
                <div className="relative flex items-center justify-between">
                  <div
                    className="h-11 w-11 rounded-xl flex items-center justify-center border transition-all duration-500"
                    style={{
                      background: `${p.color}10`,
                      borderColor: `${p.color}30`,
                      color: p.color,
                      boxShadow: `0 0 20px ${p.color}22`,
                    }}
                  >
                    <it.icon size={20} />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                    0{i + 1}
                  </div>
                </div>
                <h3 className="relative mt-6 font-display text-xl text-white">{it.title}</h3>
                <div
                  className="relative mt-2 text-[11px] font-mono uppercase tracking-[0.18em]"
                  style={{ color: p.color }}
                >
                  {it.tag}
                </div>
                <p className="relative mt-4 text-sm text-neutral-200 leading-relaxed">
                  {it.desc}
                </p>
                <div
                  className="relative mt-6 h-px w-full"
                  style={{ background: `linear-gradient(90deg, ${p.color}55, rgba(255,255,255,0.04), transparent)` }}
                />
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
